import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Edit2, Trash2, Save, ChevronLeft } from 'react-feather';
import Modal from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { xTagListsAPI, XTagList } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { Field, TextInput, fieldAria } from './ui';

interface XTagListsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewMode = 'list' | 'create' | 'edit';

const HANDLE_RE = /^[A-Za-z0-9_]{1,15}$/;
const MAX_HANDLES = 50;

// Normalize a raw handle the same way the server + tweet composer do.
function normalizeHandle(raw: string): string {
  return raw.trim().replace(/\s+/g, '').replace(/@+/g, '');
}

const XTagListsModal: React.FC<XTagListsModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();

  const [tagLists, setTagLists] = useState<XTagList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editing, setEditing] = useState<XTagList | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [handles, setHandles] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const draftRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && sessionToken) loadTagLists();
  }, [isOpen, sessionToken]);

  // Open on the list next time.
  useEffect(() => {
    if (!isOpen) {
      setViewMode('list');
      setEditing(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (viewMode === 'list') {
      setName('');
      setHandles([]);
      setDraft('');
      setEditing(null);
    } else if (viewMode === 'edit' && editing) {
      setName(editing.name);
      setHandles(editing.handles || []);
      setDraft('');
    }
  }, [viewMode, editing]);

  const loadTagLists = async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    try {
      const res = await xTagListsAPI.getTagLists(sessionToken);
      if (res.success) setTagLists(res.tagLists);
    } catch (error) {
      console.error('Error loading tag lists:', error);
      toast.showToast('Failed to load tag lists', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Commit the draft input into a chip (called on Enter, comma, space, or blur).
  const commitDraft = (): void => {
    const h = normalizeHandle(draft);
    if (!h) { setDraft(''); return; }
    if (!HANDLE_RE.test(h)) {
      toast.showToast(`"${draft.trim()}" isn't a valid X handle`, 'error');
      return;
    }
    if (handles.some((x) => x.toLowerCase() === h.toLowerCase())) {
      setDraft('');
      return; // already in the list
    }
    if (handles.length >= MAX_HANDLES) {
      toast.showToast(`A list can hold at most ${MAX_HANDLES} handles`, 'error');
      return;
    }
    setHandles((prev) => [...prev, h]);
    setDraft('');
  };

  const removeHandle = (idx: number) =>
    setHandles((prev) => prev.filter((_, i) => i !== idx));

  const onDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && handles.length > 0) {
      // backspace on an empty input pops the last chip
      removeHandle(handles.length - 1);
    }
  };

  const handleSave = async () => {
    if (!sessionToken) return;
    if (!name.trim()) {
      toast.showToast('List name is required', 'error');
      return;
    }
    // fold any un-committed draft in before saving
    const pending = normalizeHandle(draft);
    let finalHandles = handles;
    if (pending && HANDLE_RE.test(pending) && !handles.some((x) => x.toLowerCase() === pending.toLowerCase())) {
      finalHandles = [...handles, pending];
    }
    if (finalHandles.length === 0) {
      toast.showToast('Add at least one handle', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const data = { name: name.trim(), handles: finalHandles };
      if (viewMode === 'create') {
        const res = await xTagListsAPI.createTagList(sessionToken, data);
        if (res.success) {
          toast.showToast('Tag list saved.', 'success');
          onClose();
        } else {
          toast.error((res as any)?.error || 'Failed to create tag list. Please try again.');
        }
      } else if (viewMode === 'edit' && editing) {
        const res = await xTagListsAPI.updateTagList(sessionToken, editing.id, data);
        if (res.success) {
          toast.showToast('Tag list saved.', 'success');
          onClose();
        } else {
          toast.error((res as any)?.error || 'Failed to update tag list. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error saving tag list:', error);
      toast.showToast('Failed to save tag list', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!sessionToken) return;
    try {
      const res = await xTagListsAPI.deleteTagList(sessionToken, id);
      if (res.success) {
        toast.showToast('Tag list deleted', 'success');
        setDeleteConfirmId(null);
        await loadTagLists();
      } else {
        toast.error((res as any)?.error || 'Failed to delete tag list. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting tag list:', error);
      toast.showToast('Failed to delete tag list', 'error');
    }
  };

  if (!isOpen) return null;

  const chip = (label: string, onRemove?: () => void, key?: React.Key) => (
    <span
      key={key}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '4px 10px', backgroundColor: '#EEF2FF', color: '#151133',
        borderRadius: '999px', fontSize: '13px', fontWeight: 600,
      }}
    >
      @{label}
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label={`Remove @${label}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#6B7280' }}
        >
          <X size={13} />
        </button>
      )}
    </span>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDirty={viewMode !== 'list' && (name.trim().length > 0 || handles.length > 0 || draft.trim().length > 0)}
      disableClose={isSaving}
      ariaLabel="X Tag Lists"
      zIndex={10002}
      contentStyle={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '640px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '16px' : '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {viewMode !== 'list' && (
            <button onClick={() => setViewMode('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
              <ChevronLeft size={24} color="#6B7280" />
            </button>
          )}
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1F2937' }}>
            {viewMode === 'list' && 'Tag Lists'}
            {viewMode === 'create' && 'New Tag List'}
            {viewMode === 'edit' && 'Edit Tag List'}
          </h2>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
          <X size={24} color="#6B7280" />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {viewMode === 'list' ? (
          <>
            <button
              onClick={() => setViewMode('create')}
              style={{ width: '100%', padding: '16px', backgroundColor: '#f9550d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}
            >
              <Plus size={20} />
              New Tag List
            </button>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Loading tag lists...</div>
            ) : tagLists.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No tag lists yet</p>
                <p style={{ margin: 0, fontSize: '14px' }}>Save the accounts you tag most — your travel team, teammates, socials — and drop them into a post in one tap.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tagLists.map((list) => (
                  <div key={list.id} style={{ padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>{list.name}</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {(list.handles || []).slice(0, 6).map((h, i) => chip(h, undefined, i))}
                          {(list.handles || []).length > 6 && (
                            <span style={{ fontSize: '13px', color: '#6B7280', alignSelf: 'center' }}>
                              +{list.handles.length - 6} more
                            </span>
                          )}
                          {(list.handles || []).length === 0 && (
                            <span style={{ fontSize: '13px', color: '#9CA3AF' }}>No handles</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setEditing(list); setViewMode('edit'); }}
                          aria-label="Edit tag list"
                          style={{ padding: '8px', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <Edit2 size={16} color="#6B7280" />
                        </button>
                        {deleteConfirmId === list.id ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => handleDelete(list.id)} style={{ padding: '8px 12px', backgroundColor: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>Confirm</button>
                            <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '8px 12px', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmId(list.id)}
                            aria-label="Delete tag list"
                            style={{ padding: '8px', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          >
                            <Trash2 size={16} color="#EF4444" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Create / Edit form */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Field id="x-tag-list-name" label="List Name" required>
              <TextInput
                {...fieldAria('x-tag-list-name')}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Travel Team"
              />
            </Field>

            {/* The handle box is a chip entry, not a text box: the field is the
              * bordered container and the input is the caret inside it. The
              * primitive cannot express that, so it stays hand-built — but the
              * inline `outline: none` is gone, because nothing replaced it and
              * the caret was the only focusable thing in the control. */}
            <Field id="x-tag-list-handles" label="Handles" required groupLabel
              hint={`Press Enter, space, or comma to add each handle. The @ is added for you. ${handles.length}/${MAX_HANDLES}`}>
              <div
                onClick={() => draftRef.current?.focus()}
                style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minHeight: '48px', padding: '8px 12px', border: '1px solid var(--aps-border-strong)', borderRadius: 10, cursor: 'text' }}
              >
                {handles.map((h, i) => chip(h, () => removeHandle(i), i))}
                {/* Borderless ON PURPOSE. This is the draft line of a chip
                    input: the bordered box around it IS the control's frame, and
                    a second border inside it would read as a field within a
                    field. The `border: none` override outranks `.aps-input`'s
                    resting border, which is the one thing an inline style here
                    is still the right tool for. */}
                <TextInput
                  ref={draftRef}
                  id="x-tag-list-handles"
                  aria-labelledby="x-tag-list-handles-label"
                  aria-describedby="x-tag-list-handles-hint"
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onDraftKeyDown}
                  onBlur={commitDraft}
                  placeholder={handles.length === 0 ? 'Type a handle and press Enter' : 'Add another…'}
                  style={{ flex: 1, minWidth: '140px', border: 'none', background: 'transparent', fontSize: '14px', padding: '4px' }}
                />
              </div>
            </Field>

            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{ width: '100%', padding: '14px', backgroundColor: isSaving ? '#9CA3AF' : '#f9550d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}
            >
              <Save size={20} />
              {isSaving ? 'Saving...' : viewMode === 'create' ? 'Create Tag List' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default XTagListsModal;
